// ── skill markdown ──────────────────────────────────────────────────────────
// The reading renderer for a published skill. Dependency-free, server-rendered,
// and deliberately narrow: it covers exactly the subset the skill format allows
// (headings, prose, bold/italic/code/links, fences, tables, bullets, checklists,
// blockquotes, rules) and nothing else — a skill that needs more markdown than
// this is a skill that has stopped being one file.
//
// Styling lives in globals.css under `.sk-doc` so the whole document rides the
// house type scale and ink ladder rather than carrying inline styles.

import React from "react";
import { headingId } from "../../lib/skills/types";
import CopyBlock from "../CopyBlock";

function inline(text: string, keyBase: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|\*[^*\s][^*]*\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    const k = `${keyBase}-${i++}`;
    if (tok.startsWith("**")) out.push(<strong key={k}>{inline(tok.slice(2, -2), k)}</strong>);
    else if (tok.startsWith("`")) out.push(<code key={k}>{tok.slice(1, -1)}</code>);
    else if (tok.startsWith("[")) {
      const lm = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(tok);
      // ⚠️ skills now arrive from anyone (the commons), so a link may only go
      // somewhere a link should: the web, mail, this site, this page. Anything
      // else — `javascript:`, `data:` — is printed as the text it is.
      if (!lm || !/^(https?:\/\/|mailto:|\/|#)/i.test(lm[2])) out.push(tok);
      else {
        const external = /^https?:\/\//.test(lm[2]);
        out.push(
          <a key={k} href={lm[2]} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
            {inline(lm[1], k)}
          </a>,
        );
      }
    } else out.push(<em key={k}>{inline(tok.slice(1, -1), k)}</em>);
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function Check({ done }: { done: boolean }) {
  return (
    <span className="sk-check" data-on={done || undefined} aria-hidden>
      {done ? (
        <svg viewBox="0 0 14 14" fill="none">
          <path d="M3 7.3 5.8 10 11 4.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : null}
    </span>
  );
}

/** A guide may carry designed pictures of the product between its lines:
 *  `:::figure <name> <caption…>` on a line of its own. Only a surface that
 *  hands in a renderer draws them (the guides do; a skill from a stranger never
 *  gets one, so the line is simply dropped there). */
export type FigureRenderer = (name: string, caption: string, key: string) => React.ReactNode;

/** a line that opens a block of its own, so a paragraph stops before it. The
 *  branches below must each take what this lets through. */
const BLOCK_START = /^(#{1,4}\s|```|>|\||\s*[-*]\s|\d+\.\s|:::|---|\*\*\*|___)/;
/** a pipe row, and the `|---|---|` rule that sits under a table's header */
const PIPE_RULE = /^\|[\s:|-]+\|$/;

// ⚠️ EVERY pass of this loop must move `i`. A line that BLOCK_START lets through
// but no branch below takes (`:::note`, `----`, `***bold*** words`, a lone
// `| a | b |` header) used to reach the paragraph branch, which found it was a
// block start, took nothing, and went round again forever — pushing a <p> each
// time, until the process ran out of heap. It ran on the server for every
// reader and again in the browser; one such line in one brought skill took
// whichever instance rendered it down (and every request on it). So the
// paragraph branch takes its first line unconditionally, and the branches that
// recognise a block recognise the whole family of it.
export default function SkillMarkdown({ body, figure }: { body: string; figure?: FigureRenderer }) {
  const lines = body.split("\n");
  const out: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();
    const k = `b-${i}`;

    // fenced code
    if (trimmed.startsWith("```")) {
      const lang = trimmed.slice(3).trim();
      const buf: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trimStart().startsWith("```")) buf.push(lines[i++]);
      i++;
      out.push(<CopyBlock key={k} text={buf.join("\n")} lang={lang} />);
      continue;
    }

    // a designed figure (guides only)
    const fig = /^:::(?:figure|shot)\s+([a-z0-9-]+)\s*(.*)$/.exec(trimmed);
    if (fig) {
      if (figure) out.push(figure(fig[1], fig[2], k));
      i++;
      continue;
    }

    // any other `:::` line. A figure that is not spelled as one has no closing
    // line, so it is dropped like a figure nobody can draw. A named container
    // (`:::note` … `:::`, as docusaurus-style docs write them) reads as a quote;
    // an opener with no closing line, or a closer with no opener, is dropped.
    if (trimmed.startsWith(":::")) {
      let close = -1;
      if (trimmed !== ":::" && !/^:::(?:figure|shot)\b/.test(trimmed)) {
        for (let j = i + 1; j < lines.length; j++) {
          if (lines[j].trim() === ":::") {
            close = j;
            break;
          }
        }
      }
      if (close < 0) {
        i++;
        continue;
      }
      const inner = lines.slice(i + 1, close).map((l) => l.trim()).filter(Boolean);
      if (inner.length) out.push(<blockquote key={k} className="sk-quote">{inline(inner.join(" "), k)}</blockquote>);
      i = close + 1;
      continue;
    }

    // headings — h2/h3 are anchored so the contents rail can reach them
    const h = /^(#{1,4})\s+(.*)$/.exec(trimmed);
    if (h) {
      const level = h[1].length;
      const text = h[2];
      const id = headingId(text);
      if (level === 1) out.push(<h1 key={k} className="sk-h1">{inline(text, k)}</h1>);
      else if (level === 2) out.push(<h2 key={k} id={id} className="sk-h2">{inline(text, k)}</h2>);
      else if (level === 3) out.push(<h3 key={k} id={id} className="sk-h3">{inline(text, k)}</h3>);
      else out.push(<h4 key={k} className="sk-h4">{inline(text, k)}</h4>);
      i++;
      continue;
    }

    // table — a header row followed by a separator row. A row of pipes with no
    // separator under it (a template header the skill fills in later) is still
    // a table: its header, and whatever pipe rows follow it.
    const ruled = PIPE_RULE.test(lines[i + 1]?.trim() ?? "");
    if (trimmed.startsWith("|") && (ruled || (trimmed.length > 1 && trimmed.endsWith("|")))) {
      const cells = (row: string) =>
        row.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
      const head = cells(lines[i]);
      i += ruled ? 2 : 1;
      const rows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) rows.push(cells(lines[i++]));
      out.push(
        <div key={k} className="sk-table-wrap">
          <table>
            <thead>
              <tr>{head.map((c, ci) => <th key={ci}>{inline(c, `${k}-h${ci}`)}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri}>{r.map((c, ci) => <td key={ci}>{inline(c, `${k}-${ri}-${ci}`)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    // blockquote
    if (trimmed.startsWith(">")) {
      const buf: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith(">")) buf.push(lines[i++].trim().replace(/^>\s?/, ""));
      out.push(<blockquote key={k} className="sk-quote">{inline(buf.join(" "), k)}</blockquote>);
      continue;
    }

    // rule — three of one mark or more (`----` is a rule too)
    if (/^([-*_])\1{2,}\s*$/.test(trimmed)) {
      out.push(<hr key={k} className="sk-rule" />);
      i++;
      continue;
    }

    // bullets + checklists (one run, continuation lines folded in)
    if (/^\s*[-*]\s+/.test(line)) {
      const items: { done: boolean | null; text: string }[] = [];
      while (i < lines.length && (/^\s*[-*]\s+/.test(lines[i]) || (items.length > 0 && /^\s{4,}\S/.test(lines[i])))) {
        const m2 = /^\s*[-*]\s+(\[([ xX])\]\s+)?(.*)$/.exec(lines[i]);
        if (m2) items.push({ done: m2[1] ? m2[2].toLowerCase() === "x" : null, text: m2[3] });
        else if (items.length) items[items.length - 1].text += ` ${lines[i].trim()}`;
        i++;
      }
      const isCheck = items.some((it) => it.done !== null);
      out.push(
        <ul key={k} className={isCheck ? "sk-checks" : "sk-list"}>
          {items.map((it, ii) => (
            <li key={ii}>
              {it.done !== null && <Check done={it.done} />}
              <span>{inline(it.text, `${k}-${ii}`)}</span>
            </li>
          ))}
        </ul>,
      );
      continue;
    }

    // numbered steps — the order is the point, so the number is kept
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && (/^\s*\d+\.\s+/.test(lines[i]) || (items.length > 0 && /^\s{3,}\S/.test(lines[i])))) {
        const m3 = /^\s*\d+\.\s+(.*)$/.exec(lines[i]);
        if (m3) items.push(m3[1]);
        else items[items.length - 1] += ` ${lines[i].trim()}`;
        i++;
      }
      out.push(
        <ol key={k} className="sk-steps">
          {items.map((t, ii) => (
            <li key={ii}>
              <span className="sk-steps__n" aria-hidden>{ii + 1}</span>
              <span>{inline(t, `${k}-${ii}`)}</span>
            </li>
          ))}
        </ol>,
      );
      continue;
    }

    // blank
    if (!trimmed) {
      i++;
      continue;
    }

    // paragraph — folds soft-wrapped lines back into one. The first line is
    // taken whatever it looks like (see the note above the loop): only the
    // lines AFTER it may be refused for opening a block of their own.
    const buf: string[] = [trimmed];
    i++;
    while (i < lines.length && lines[i].trim() && !BLOCK_START.test(lines[i].trim())) {
      buf.push(lines[i++].trim());
    }
    out.push(<p key={k} className="sk-p">{inline(buf.join(" "), k)}</p>);
  }

  return <div className="sk-doc">{out}</div>;
}
